import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUp, ArrowDown, Loader2, Pencil, Trash2 } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { PageShell } from '@/components/layout/PageShell'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { ListPaginationBar, ListPaginationControls } from '@/components/ui/ListPaginationBar'
import { SystemAnnouncementEditModal } from '../components/SystemAnnouncementEditModal'
import {
  deleteSystemAnnouncement,
  getSystemAnnouncement,
  getSystemAnnouncements,
  saveSystemAnnouncement,
} from '../services/systemAnnouncementService'
import type {
  SystemAnnouncement,
  SystemAnnouncementListParams,
  SystemAnnouncementSortKey,
  SystemAnnouncementSubmitPayload,
  SortDirection,
} from '../types'
import { formatSystemAnnouncementStartEndRange } from '../utils/formatSystemAnnouncementListDate'

const DEFAULT_PARAMS: SystemAnnouncementListParams = {
  page: 1,
  perPage: 15,
  sortBy: 'desc',
  orderBy: 'start_date',
}

const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const
const PAGINATION_TEST_ID_PREFIX = 'sa-list'

export function SystemAnnouncementListPage() {
  const [announcements, setAnnouncements] = useState<SystemAnnouncement[]>([])
  const [params, setParams] = useState<SystemAnnouncementListParams>(DEFAULT_PARAMS)
  const [pagination, setPagination] = useState({ current: 1, last: 1 })
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<SystemAnnouncement | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SystemAnnouncement | null>(null)
  const mountedRef = useRef(false)

  const currentPage = pagination.current
  const lastPage = pagination.last
  const showNumericPagination = totalRecords > 0

  const load = useCallback(async (next: SystemAnnouncementListParams) => {
    setLoading(true)
    try {
      const response = await getSystemAnnouncements(next)
      setAnnouncements(response.data ?? [])
      setPagination({
        current: response.current ?? 1,
        last: Math.max(1, response.last ?? 1),
      })
      setTotalRecords(response.total ?? 0)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  const applyParams = useCallback(
    (next: SystemAnnouncementListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: SystemAnnouncementSortKey) => {
    const nextDir: SortDirection =
      params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir, page: 1 })
  }

  const goToPage = (pageNo: number) => applyParams({ ...params, page: pageNo })

  const changePerPage = (perPage: number) =>
    applyParams({ ...params, page: 1, perPage })

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }

  const openEdit = async (row: SystemAnnouncement) => {
    if (!row.sa_id) return
    try {
      const detail = await getSystemAnnouncement(row.sa_id)
      setEditTarget(detail)
      setEditOpen(true)
    } catch (error) {
      showApiError(error)
    }
  }

  const openDelete = (row: SystemAnnouncement) => {
    setDeleteTarget(row)
  }

  const handleSubmit = async (payload: SystemAnnouncementSubmitPayload, saId?: number) => {
    setSubmitting(true)
    try {
      await saveSystemAnnouncement(payload, saId)
      trackApplicationEvent('system_announcement_saved', 'system')
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget?.sa_id) return
    try {
      await deleteSystemAnnouncement(deleteTarget.sa_id)
      showSuccess('Announcement Deleted Successfully!')
      setDeleteTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    }
  }

  const SortIndicator = ({ column }: { column: SystemAnnouncementSortKey }) => {
    if (params.orderBy !== column) return <ArrowDown className="h-3 w-3 opacity-20" />
    return params.sortBy === 'asc' ? (
      <ArrowUp className="h-3 w-3" />
    ) : (
      <ArrowDown className="h-3 w-3" />
    )
  }

  return (
    <PageShell
      title="SYSTEM ANNOUNCEMENT"
      headerAction={
        <button
          type="button"
          onClick={openAdd}
          className={primaryEntityActionButtonClass}
        >
          Add System Announcement
        </button>
      }
    >
      <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#1597ff]" />
          </div>
        ) : (
          <>
            {showNumericPagination && (
              <div
                data-testid={`${PAGINATION_TEST_ID_PREFIX}-header-bar`}
                className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-4"
              >
                <div
                  data-testid={`${PAGINATION_TEST_ID_PREFIX}-header-summary`}
                  className="text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm"
                >
                  Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                  <span className="font-bold text-slate-900">{lastPage}</span>
                  <span className="mx-2 text-slate-300">|</span>
                  Total <span className="font-bold text-slate-900">{totalRecords}</span> Records
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
                  <ListPaginationControls
                    currentPage={currentPage}
                    totalPages={lastPage}
                    onPageChange={goToPage}
                    testIdPrefix={PAGINATION_TEST_ID_PREFIX}
                    placement="header"
                  />
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className={applicationStyles.listingTable}>
                <thead>
                  <tr>
                    <th aria-sort={params.orderBy === 'announcement_topic' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button
                        type="button"
                        className={applicationStyles.sortHeaderButton}
                        onClick={() => toggleSort('announcement_topic')}
                      >
                        <span className={applicationStyles.sortHeaderLabel}>Topic</span>
                        <SortIndicator column="announcement_topic" />
                      </button>
                    </th>
                    <th aria-sort={params.orderBy === 'announcement_description' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button
                        type="button"
                        className={applicationStyles.sortHeaderButton}
                        onClick={() => toggleSort('announcement_description')}
                      >
                        <span className={applicationStyles.sortHeaderLabel}>Description</span>
                        <SortIndicator column="announcement_description" />
                      </button>
                    </th>
                    <th aria-sort={params.orderBy === 'start_date' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button
                        type="button"
                        className={applicationStyles.sortHeaderButton}
                        onClick={() => toggleSort('start_date')}
                      >
                        <span className={applicationStyles.sortHeaderLabel}>Start-End Date</span>
                        <SortIndicator column="start_date" />
                      </button>
                    </th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {announcements.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                        No system announcements.
                      </td>
                    </tr>
                  ) : (
                    announcements.map((ann) => (
                      <tr key={ann.sa_id}>
                        <td className="font-medium text-slate-900">{ann.announcement_topic}</td>
                        <td
                          className="max-w-md text-slate-700 truncate"
                          dangerouslySetInnerHTML={{
                            __html: (ann.announcement_description || '').substring(0, 250),
                          }}
                        />
                        <td className="text-slate-700 whitespace-nowrap">
                          {formatSystemAnnouncementStartEndRange(ann.start_date, ann.end_date)}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEdit(ann)}
                              className="p-1.5 rounded hover:bg-blue-50 text-[#1597ff]"
                              aria-label="Edit"
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openDelete(ann)}
                              className="p-1.5 rounded hover:bg-red-50 text-red-500"
                              aria-label="Delete"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {showNumericPagination && (
          <ListPaginationBar
            placement="footer"
            currentPage={currentPage}
            lastPage={lastPage}
            totalRecords={totalRecords}
            perPage={params.perPage ?? 15}
            onPageChange={goToPage}
            onPerPageChange={changePerPage}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix={PAGINATION_TEST_ID_PREFIX}
            barDataTestId={`${PAGINATION_TEST_ID_PREFIX}-footer-bar`}
          />
        )}
          </>
        )}
      </div>

      <SystemAnnouncementEditModal
        isOpen={editOpen}
        announcement={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete System Announcement"
        message="Are You Sure To Delete this Announcement ?"
        primaryLabel="Delete"
        onPrimary={confirmDelete}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </PageShell>
  )
}
